import { z } from '@/utils/zod'

export const StorageResourceSchema = z.object({
    resource: z.string().min(1),
    fileName: z.string().max(128).optional(),
    contentType: z.string().max(75).optional(),
    fileSizeBytes: z.number().int().nonnegative().optional(),
})

export type StorageResource = z.infer<typeof StorageResourceSchema>

export const StorageResourcesSchema = z.array(
    StorageResourceSchema.extend({
        id: z.string().min(6).max(6),
    })
)

export type StorageResources = z.infer<typeof StorageResourcesSchema>

export const InputStorageResourcePathSchema = z.string().refine(
    (val) => {
        return !!val
    },
    {
        params: {
            i18n: { key: 'custom.resourceRequired' },
        },
    }
)

export type InputStorageResourcePath = z.infer<typeof InputStorageResourcePathSchema>

export const InputStorageResourceSchema = z.object({
    fileName: StorageResourceSchema.shape.fileName,
    resource: InputStorageResourcePathSchema,
})

export type InputStorageResource = z.infer<typeof InputStorageResourceSchema>

export const InputStorageResourcesSchema = z.array(
    InputStorageResourceSchema.extend({
        id: z.string().min(6).max(6).optional(),
    })
)

export type InputStorageResources = z.infer<typeof InputStorageResourcesSchema>

export type ExtractedStorageResource = {
    resource: string
    url: string
    fileSizeBytes?: number
    fileName?: string
    contentType?: string
}

export const extractStorageResource = (resource: StorageResource | null): ExtractedStorageResource | null => {
    return resource?.resource
        ? {
              resource: resource.resource,
              url: resource.resource,
              fileSizeBytes: resource.fileSizeBytes,
              fileName: resource.fileName,
              contentType: resource.contentType,
          }
        : null
}

export const createInputStorageResource = (resource: ExtractedStorageResource | null): InputStorageResource | null => {
    if (!resource?.resource) {
        return null
    }

    return {
        resource: resource.resource || '',
        fileName: resource.fileName,
    }
}

export type ExtractedStorageResources = Array<{ id?: string } & ExtractedStorageResource>

export const extractStorageResources = (resources: StorageResources): ExtractedStorageResources =>
    resources.map(({ id, resource, fileName, fileSizeBytes, contentType }) => ({
        id,
        resource,
        url: resource,
        fileSizeBytes,
        fileName,
        contentType,
    }))

export const createInputStorageResources = (resources: ExtractedStorageResources): InputStorageResources =>
    resources.map(({ id, resource, fileName }) => ({
        id,
        resource,
        fileName,
    }))
